import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from "react-redux";
import {
  departmentsUser,
  getLatestIncidentRefNo
} from "../services/api";
import { setAlert } from "../store/slices/alertSlice";
import "../styles/incident.css"; // Assuming you have a dedicated CSS file

const Part1 = ({ data, onChange, disabled = false, currentUser, maskName = false }) => {
  const [departments, setDepartments] = useState([]);
  const [refNo, setRefNo] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [personInvolved, setPersonInvolved] = useState([]);
  const dispatch = useDispatch();
  const onChangeRef = useRef(onChange);
  const dataRef = useRef(data);
  const loggedInUserName = useMemo(
    () => currentUser?.username || currentUser?.name || currentUser?.fullName || '',
    [currentUser]
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [departmentsResponse, latestRefNoResponse] = await Promise.all([
          departmentsUser(),
          getLatestIncidentRefNo(),
        ]);
        setDepartments(departmentsResponse.data);
        const nextRefNo = data.refNo || latestRefNoResponse.data.refNo;
        setRefNo(nextRefNo);

        const today = new Date().toISOString().split('T')[0];
        const nextDate = data.date ? new Date(data.date).toISOString().split('T')[0] : today;
        setCurrentDate(nextDate);

        if (!disabled && (!data.refNo || !data.date || !data.name)) {
          onChangeRef.current({
            ...dataRef.current,
            refNo: nextRefNo,
            date: nextDate,
            name: loggedInUserName
          });
        }


        // Set initial personInvolved from provided data or default
        setPersonInvolved(data.personInvolved || []);
      } catch (error) {
        dispatch(setAlert({ message: 'Error fetching data', type: 'danger' }));
      }
    };
    fetchInitialData();
  }, [dispatch, data.date, data.personInvolved, data.refNo, data.name, disabled, loggedInUserName]);

  // This will update the refNo whenever the data changes
  useEffect(() => {
    setRefNo(data.refNo);
  }, [data.refNo]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextDate = name === 'date' ? value : currentDate;

    if (name === 'date') {
      setCurrentDate(value);
    }

    const updatedData = {
      ...data,
      [name]: type === 'checkbox' ? checked : value,
      refNo, // Ensure `refNo` is always included
      date: nextDate, // Ensure `date` is always included
    };

    if (name === 'personInvolved') {
      const newPersonInvolved = checked
        ? [...personInvolved, value]
        : personInvolved.filter((item) => item !== value);

      setPersonInvolved(newPersonInvolved);
      updatedData.personInvolved = newPersonInvolved;
    }

    if (name === 'ageYears' || name === 'ageMonths') {
      updatedData[name] = value === '' ? '' : Number(value);
      updatedData.age = name === 'ageYears' ? Number(value || 0) : Number(data.ageYears || data.age || 0);
    }

    onChange(updatedData);
  };


  return (
    <div className="form-container compact">
      <div className="form-row">
        <div className="form-control">
          <label htmlFor="refNo">Reference Number</label>
          <input
            type="text"
            id="refNo"
            name="refNo"
            value={data.refNo || refNo}
            disabled
          />
        </div>
        <div className="form-control">
          <label htmlFor="department">Department <span className="required-mark">*</span></label>
          <select
            id="department"
            name="department"
            value={data.department || ''}
            onChange={handleInputChange}
            disabled={disabled}
          >
            <option value="" disabled>Select Department</option>
            {departments.map((dept) => (
              <option key={dept._id} value={dept._id}>
                {dept.departmentName} ({dept.departmentCode})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-control">
          <label htmlFor="date">Date of Occurrence <span className="required-mark">*</span></label>
          <input
            type="date"
            id="date"
            name="date"
            value= {currentDate}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>
        <div className="form-control created-by-control">
          <label htmlFor="name">Created By <span className="required-mark">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            value={maskName ? '****' : data.name || loggedInUserName || ''}
            onChange={handleInputChange}
            disabled
          />
        </div>
        <div className="form-control emr-control">
          <label htmlFor="emrNoName">EMR No/Name</label>
          <input
            type="text"
            id="emrNoName"
            name="emrNoName"
            value={data.emrNoName || ''}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="form-row">
      <div className="form-control person-involved">
          <label>Person Involved <span className="required-mark">*</span></label>
          <div className="checkbox-group">
            {['dayCare', 'outpatient', 'visitor', 'employee', 'other'].map((item) => (
              <label key={item}>
                <input
                  type="checkbox"
                  name="personInvolved"
                  value={item}
                  checked={personInvolved.includes(item)}
                  onChange={handleInputChange}
                  disabled={disabled}
                />
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </label>
            ))}
          </div>
        </div>

        <div className="form-control gender">
          <label>Gender</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="gender"
                value="male"
                checked={data.gender === 'male'}
                onChange={handleInputChange}
                disabled={disabled}
              />
              Male
            </label>
            <label>
              <input
                type="radio"
                name="gender"
                value="female"
                checked={data.gender === 'female'}
                onChange={handleInputChange}
                disabled={disabled}
              />
              Female
            </label>
          </div>
        </div>

        <div className="form-control age-control">
          <label>Age</label>
          <div className="age-group">
            <input
              type="number"
              id="ageYears"
              name="ageYears"
              value={data.ageYears ?? data.age ?? ''}
              onChange={handleInputChange}
              min={0}
              max={120}
              disabled={disabled}
            />
            <span>Years</span>
            <input
              type="number"
              id="ageMonths"
              name="ageMonths"
              value={data.ageMonths ?? ''}
              onChange={handleInputChange}
              min={0}
              max={11}
              disabled={disabled}
            />
            <span>Months</span>
          </div>
        </div>
      </div>

      <div className="form-row">
        <label>
          <input
            type="checkbox"
            name="medicationError"
            checked={data.medicationError || false}
            onChange={handleInputChange}
            disabled={disabled}
          />
          MEDICATION ERROR
        </label>
      </div>

      {data.medicationError && (
        <div className="form-row">
          <div className="form-control">
            <label htmlFor="drugName">Drug Name</label>
            <input
              type="text"
              id="drugName"
              name="drugName"
              value={data.drugName || ''}
              onChange={handleInputChange}
              disabled={disabled}
            />
          </div>
          <div className="form-control">
            <label htmlFor="dose">Dose</label>
            <input
              type="text"
              id="dose"
              name="dose"
              value={data.dose || ''}
              onChange={handleInputChange}
              disabled={disabled}
            />
          </div>
        </div>
      )}

      <div className="form-row grid">
        {[
          'NEEDLE & SHARPS',
          'INTRAVENOUS COMPLICATIONS',
          'OCCUPATIONAL HEALTH AND SAFETY',
          'SAFETY VARIANCE',
          'RADIATION SAFETY VARIANCE',
          'PATIENT FALL',
          'BEHAVIOR VARIANCE',
          'PROCEDURAL VARIANCE',
          'SECURITY VARIANCE',
          'NARCOTIC MEDICATION VARIANCE',
          'CLINICAL VARIANCE',
        ].map((category) => (
          <label key={category} className="category-checkbox">
            <input
              type="checkbox"
              name={category.replace(/\s+/g, '').toLowerCase()}
              checked={data[category.replace(/\s+/g, '').toLowerCase()] || false}
              onChange={handleInputChange}
              disabled={disabled}
            />
            {category}
          </label>
        ))}
      </div>

      <div className="form-row">
        <div className="form-control full-width">
          <label htmlFor="othersSpecify">OTHERS (Specify)</label>
          <input
            type="text"
            id="othersSpecify"
            name="othersSpecify"
            value={data.othersSpecify || ''}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="form-section">
        <h6 className="section-subheading">EQUIPMENT RELATED</h6>
        <div className="form-row">
          <div className="form-control half-width">
            <label htmlFor="equipmentName">Equipment Name</label>
            <input
              type="text"
              id="equipmentName"
              name="equipmentName"
              value={data.equipmentName || ''}
              onChange={handleInputChange}
              className="form-control"
              disabled={disabled}
            />
          </div>
          <div className="form-control half-width">
            <label htmlFor="equipmentAssetId">Equipment Asset ID #</label>
            <input
              type="text"
              id="equipmentAssetId"
              name="equipmentAssetId"
              value={data.equipmentAssetId || ''}
              onChange={handleInputChange}
              className="form-control"
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-control full-width">
          <label htmlFor="factualDescription">Factual Description <span className="required-mark">*</span></label>
          <textarea
            id="factualDescription"
            name="factualDescription"
            value={data.factualDescription || ''}
            onChange={handleInputChange}
            className="form-control large-textarea"
            disabled={disabled}
          />
        </div>
      </div><div className="form-row">
        <div className="form-control full-width">
          <label htmlFor="immediateAction">Immediate Action <span className="required-mark">*</span></label>
          <textarea
            id="immediateAction"
            name="immediateAction"
            value={data.immediateAction || ''}
            onChange={handleInputChange}
            className="form-control large-textarea"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export default Part1;
